import React from 'react';

const NyxNoCodeStudentGuide = () => {
    return (
        <div className="document">
            <h1>📘 NYX NoCode Student Guide</h1>
            <p>
                Welcome to NYX NoCode! This guide will help you turn your ideas into interactive digital projects without needing to write any code.
                Let's dive into the basics of how NYX NoCode works and explore some of the creative tools at your fingertips.
            </p>
            <p>When you click Create Now from the Gallery, you enter the Create section of NYX NoCode, where you will find your Prompt Pad and Doodle Pad.</p>

            <h2>The Gallery</h2>
            <p>
                When you first sign in to NYX NoCode, you land in the Gallery. This is where all of your files are stored including those that you generate
                and those that you upload yourself. The Gallery automatically has two folders, including a Mena School District folder with school images you
                can use, and a doodles folder for any doodles you create.
            </p>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/gallery-page1.jpeg" alt="Gallery Main Interface" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
                <p style={{fontStyle: 'italic', fontSize: '0.9em', marginTop: '10px'}}>The Gallery main screen with the CREATE NOW button and your folders.</p>
            </div>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/overview-page2.jpeg" alt="The Gallery" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
                <p style={{fontStyle: 'italic', fontSize: '0.9em', marginTop: '10px'}}>The Gallery is a space where everything your team creates is automatically saved.</p>
            </div>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/gallery-page2.jpeg" alt="Gallery File Management" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
                <p style={{fontStyle: 'italic', fontSize: '0.9em', marginTop: '10px'}}>Your Gallery organizes Images, Web Files, and Videos with options to copy, download, edit, and share.</p>
            </div>

            <h2>How to Sign In and Out of NYX NoCode</h2>
            <p>
                You will receive a team email address from your teacher to use to sign in. Follow the steps below to learn how to access
                and exit your NYX NoCode account efficiently.
            </p>



            <p><strong>Steps to Sign In:</strong></p>
            <ol>
                <li>Go to the NYX NoCode website.</li>
                <li>Click on the "Sign In" button located at the top center of the Gallery.</li>
                <li>Follow the prompts, using the ID and password given to you by your teacher. You are using a TAP, or Temporary Access Pass, that is good for 30 days. It can be renewed by asking your teacher.</li>
            </ol>

            <p><strong>Steps to Sign Out:</strong></p>
            <ol>
                <li>Click on the Sign Out button, located at the top of the Gallery and the Studio.</li>
                <li>Close all browser windows.</li>
            </ol>
            <p><strong>Tip: </strong>
           There is a "Signed in as" box at the top of the Gallery.</p>
            <p>
                If you encounter any issues or have further questions, please contact your teacher for assistance.
            </p>

            <h2>The Studio: Your Creative Tools</h2>
            <p>
                The Studio contains different "Pads"—each one is a special tool for creating different kinds of content. Let's explore them!
            </p>
            <p>
                To get to the Studio, click <strong>**CREATE NOW**</strong> in the center of the Gallery.
            </p>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/overview-page3.jpeg" alt="The Studio" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
                <p style={{fontStyle: 'italic', fontSize: '0.9em', marginTop: '10px'}}>The Studio is shown here with Justin Bearcat's Launch Pad open for help documents or chat.</p>
            </div>

            <h3>1. Prompt Pad - Turn Your Words Into Reality</h3>
            <p>
                The Prompt Pad is the heart of NYX NoCode. It's where you type what you want to create, and AI brings it to life!
            </p>

            <p><strong>What is a Prompt?</strong></p>
            <p>
                A prompt is a description of what you want to create. For example, you might say, "Mountain bike." NYX NoCode will take this prompt and
                transform it into a web page that explains mountain bikes, in seconds. Or you could say "35mm photograph of a mountain bike." Or even
                "A newspaper article about a brilliant student team that won the Mena NoCode hackathon with their idea for a dog sitting service for mountain bikers."
                Your prompt can be short or really long, just remember to Save Prompt if you are working on it, so you can find it again. You can include your own text,
                or let NYX NoCode write your text. You can include links to other creations, which gives you the ability to make multi-page websites.
            </p>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/prompt-pad-page1.jpeg" alt="Prompt Pad Interface" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
            </div>

            <p><strong>How to Write a Great Prompt</strong></p>
            <p>
                Writing a great prompt, or prompt engineering, involves describing what you want in a way that the AI will understand. Sometimes this means you have
                to break your idea down into multiple prompts. Think about what you want your project to look like, its purpose, and any specific features you'd like
                it to have. Keep it clear and descriptive. You can ask Justin Bearcat for help in creating prompts.
            </p>

            <p><strong>The Generate Button</strong></p>
            <p>
                The magic happens when you press Generate. That's when your prompt is handed to NYX NoCode's powerful AI and beautiful artwork and web pages are
                returned.
            </p>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/prompt-pad-page2.jpeg" alt="Generated Content" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
                <p style={{fontStyle: 'italic', fontSize: '0.9em', marginTop: '10px'}}>After generating, you'll see your generated content with options to view, copy, or add to your prompt.</p>
            </div>

            <p><strong>Using the Gallery Files in Your Prompts:</strong></p>
            <p>
                You can select files from your Gallery and then click on their icons in the Prompt Pad to include them in your creation.
                Sometimes the file names alone are descriptive enough to make something awesome!
            </p>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/prompt-pad-page4.jpeg" alt="Copy Image to Prompt" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
                <p style={{fontStyle: 'italic', fontSize: '0.9em', marginTop: '10px'}}>Here we have clicked "Copy Image to Prompt" then "Copy HTML" to prompt.</p>
            </div>

            <p><strong>NYX NoCode Creates Artifacts Too!</strong></p>
            <p>
                When you generate content, NYX NoCode doesn't just create web pages—it sometimes creates unused image artifacts that get automatically saved to your Gallery! These artifacts can be reused
                in future projects or combined with other elements to create even more amazing content.
            </p>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/kilroy@uark.edu-show_me_a_magical_web_effect_that_will_stun_my_visitors_1-20251102-071247.png" alt="Magical Web Effect Artifact" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
                <p style={{fontStyle: 'italic', fontSize: '0.9em', marginTop: '10px'}}>When a "web effect" was requested, in addition to the css for the web page (a "web effect") NYX NoCode created an image of a spider web with a special effect (a literal "web effect") and stored this artifact in the Gallery.</p>
            </div>

            <h3>2. Doodle Pad - Draw Your Ideas</h3>
            <p><strong>What's the Doodle Pad?</strong></p>
            <p>
                The Doodle Pad lets you draw ideas visually, and then those doodles are analyzed in real time and a prompt is generated in the box. You can experiment
                with colors, line styles, and special effects to create eye-catching designs. If you hit the Generate button right after you doodle, you will get a web
                page of your doodles. If you delete everything in the prompt box except the final description of your doodle, that will generate an image of what you drew.
            </p>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/doodle-pad-page1.jpeg" alt="Doodle Pad" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
            </div>

            <ul>
                <li><strong>Color Options:</strong> Choose from preset colors or pick any hue using the square color picker.</li>
                <li><strong>Line Customization:</strong> Adjust line width and opacity to bring your design to life.</li>
                <li><strong>Special Modes:</strong> Try Rainbow Mode, Mirror Draw, Kaleidoscope, Glow Effect, Spirograph, and more for unique effects.</li>
                <li><strong>Shape Stamp:</strong> Add shapes like circles and squares to structure your designs. You need to select Shape Stamp again to make additional shapes.</li>
            </ul>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/doodle-pad-page3.jpeg" alt="Doodle Pad Drawing Tools" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
                <p style={{fontStyle: 'italic', fontSize: '0.9em', marginTop: '10px'}}>The Doodle Pad's comprehensive drawing tools let you customize every aspect of your creation.</p>
            </div>

            <p>
                The Doodle Pad is perfect if you like to express your ideas visually. Draw what you're imagining, and NYX NoCode will help turn it into digital content!
            </p>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/doodle-pad-page2.jpeg" alt="Doodle Pad Saves Automatically" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
                <p style={{fontStyle: 'italic', fontSize: '0.9em', marginTop: '10px'}}>Every step of the doodling process is saved automatically in the Doodle Folder in the Gallery.</p>
            </div>

            <h3>3. 3D Pad - Build 3D Objects</h3>
            <p>
                Want to create three-dimensional shapes? The 3D Pad lets you design 3D objects and even record videos of them!
            </p>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/3d-pad-page1.jpeg" alt="3D Pad Interface" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
                <p style={{fontStyle: 'italic', fontSize: '0.9em', marginTop: '10px'}}>The 3D Pad does not use artificial intelligence, but it was created using NYX NoCode.</p>
            </div>

            <p><strong>How to Use It:</strong></p>
            <ul>
                <li>Choose a shape from the dropdown menu</li>
                <li>Experiment with the number of shapes</li>
                <li>Add colors or keep them transparent</li>
                <li>Adjust sizing options to get just the right look</li>
                <li>You can even "skin" your objects with textures or images!</li>
            </ul>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/3d-pad-page2.jpeg" alt="3D Pad Example" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
                <p style={{fontStyle: 'italic', fontSize: '0.9em', marginTop: '10px'}}>This non-AI torus knot is skinned with an AI-generated image.</p>
            </div>

            <h3>4. Speech Pad - Make Your Computer Talk</h3>
            <p>
                The Speech Pad turns your text into spoken words! You can paste or type text, or even record your own voice.
            </p>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/speech-pad-page1.jpeg" alt="Speech Pad" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
            </div>

            <p><strong>Features:</strong></p>
            <ul>
                <li>Choose from 41 different AI voices</li>
                <li>Test different voices using the "Sample" button</li>
                <li>Create custom speech audio from any text you provide</li>
                <li>Perfect for presentations, audio guides, or accessibility features</li>
            </ul>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/speech-pad-page2.jpeg" alt="Generated Speech" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
                <p style={{fontStyle: 'italic', fontSize: '0.9em', marginTop: '10px'}}>After generating speech, you can download the audio file or add it to your prompt.</p>
            </div>

            <h3>5. Video Pad - Combine Video Clips</h3>
            <p>
                The Video Pad lets you combine multiple video clips of the same file type into one video.
            </p>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/video-pad-page1.jpeg" alt="Video Pad" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
            </div>

            <p><strong>What You Can Do:</strong></p>
            <ul>
                <li>Upload videos from your computer or select from the Gallery</li>
                <li>Combine multiple clips in the order you want</li>
                <li>Create project presentations or video stories</li>
                <li>All clips must be the same file type</li>
            </ul>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/video-pad-page2.jpeg" alt="Video Pad with Audio" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
                <p style={{fontStyle: 'italic', fontSize: '0.9em', marginTop: '10px'}}>The Video Pad lets you add multiple video clips and optional audio to create your final video.</p>
            </div>

            <h3>6. Launch Pad - Get Help from Justin Bearcat</h3>
            <p>
                The Launch Pad is where you can chat with Justin Bearcat, an AI assistant designed to help you with the hackathon!
            </p>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/launch-pad-page1.jpeg" alt="Launch Pad with Justin Bearcat" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
            </div>

            <p><strong>Justin Can Help With:</strong></p>
            <ul>
                <li>Planning your project</li>
                <li>Creating better prompts</li>
                <li>Answering questions about how to use NYX NoCode</li>
                <li>Giving you creative ideas</li>
            </ul>
            <p>
                Just click "Show Justin Bearcat's Chatbox" and start typing your questions. Justin is friendly and always ready to help!
            </p>

            <h2>The Help Box</h2>
            <p><strong>What's in the Help Box?</strong></p>
            <p>
                In the Help Box you will find both links to helpful resources and Justin Bearcat, an AI designed specifically to help you with the Mena NoCode Hackathon.
                Justin Bearcat can help you with planning, creating prompts, and in how to use NYX NoCode.
            </p>

            <h2>Shared Gallery - Show Off Your Work</h2>
            <p>
                The Shared Gallery is a special space where student projects are displayed for everyone to see and review!
            </p>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/shared-gallery-page1.jpeg" alt="Shared Gallery" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
            </div>

            <p><strong>How It Works:</strong></p>
            <ul>
                <li>Each team can share ONE HTML file to the Shared Gallery</li>
                <li>Other students can view and review your projects</li>
                <li>You can review other teams' projects by rating them in 10 different categories</li>
                <li>This is a great way to get inspired and learn from each other!</li>
            </ul>

            <h2>Troubleshooting and Tips</h2>

            <p><strong>Stuck on an idea?</strong></p>
            <p>
                Start with a simple concept, and expand from there. You can always edit your project as new ideas come up.
            </p>

            <p><strong>Is that really a word? What's up with the spelling errors?</strong></p>
            <p>
                Usually you will find this only in images. The AI image generators are using tokenized, or shortened, versions of words and phrases,
                and sometimes they just produce total nonsense. But sometimes the image generator gets a word right. When you need words, it is best to
                create text to go along with your images.
            </p>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img src="/guide-images/overview-page1.jpeg" alt="Fun AI Mistakes" style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
                <p style={{fontStyle: 'italic', fontSize: '0.9em', marginTop: '10px'}}>Fun AI mistakes – like this student who has a "leg up" on his classmates – shows that we need to examine AI with a critical eye, even as it improves.</p>
            </div>

            <p><strong>Do I need to fact check AI's work?</strong></p>
            <p>
                Yes! It is always best to verify the output of AI using another source if you have any questions at all about whether or not it is right.
                Large Language Models, or LLMs, which drive systems like this are always guessing what you are going to say next. If they don't have enough
                facts for a good guess, they can make things up! This is called hallucination, and you should always be aware that generative AI systems like
                LLMs can frequently hallucinate. There are settings that AI engineers can adjust that help to reduce hallucinations but they can also reduce creativity.
            </p>

            <p><strong>I'm stuck! How can I get down to the Save Page button?</strong></p>
            <p>
                If buttons scroll off the page, you can try making the prompt text input area smaller by dragging the lower right hand corner up.
                Or you can click in the text input area and then press the tab key to move from button to button.
            </p>

            <p><strong>I made a doodle. Now how do I get it to stop showing and just generate the drawing?</strong></p>
            <p>
                Just delete the link that goes to the doodle from the prompt, so you have something left like "colorful rain drops," then hit Send.
                Alternatively, if you don't do anything after you make a doodle and hit send, all the steps to create the doodle will show up on the web page.
            </p>

            <p><strong>How do I get my doodle description to go back into my prompt?</strong></p>
            <p>
                Just Save Doodle again and the image will be analyzed and the prompt will go back into the input box.
            </p>

            <p><strong>Can I save prompts and reuse them later? Where do they go?</strong></p>
            <p>
                Yes, you can save your prompts by clicking the Save Prompt button BEFORE you hit Send. You can recall, edit, and save them again.
                Also, the prompts are permanently stored in your Gallery as text files, so you can find them again there as well.
            </p>

            <p><strong>Can I use NYX NoCode to build non-AI systems?</strong></p>
            <p>
                YES! Imagine a world where you build your tools as you need them, like in Minecraft. That is what NYX NoCode can do for you.
                If you need a word processor, ask NYX to create one! If you need a calculator with nice big buttons and a theme that you like,
                ask NYX to make an interactive order form. We don't know what all NYX NoCode will create yet and we are hoping you push the boundaries.
            </p>

            <p><strong>Can I use NYX NoCode to build AI systems?</strong></p>
            <p>
                Well, yes and no. Using an AI system with the applications you build will require a more complex setup than NYX NoCode alone.
                You could build the application portion and then link to what is called an API, or application programming interface, for your AI,
                but that is outside the scope of this hackathon.
            </p>

            <p><strong>What programming languages does NYX NoCode use?</strong></p>
            <p>
                Primarily, NYX NoCode develops web applications, which involve HTML. NYX NoCode's stack is HTML5 and javascript with embedded styles.
                HTML, or Hypertext Markup Language, is how a web browser knows what to do with the information that comes into it. CSS3, or Cascading Style Sheets,
                are often used to provide the design like font size, alignment, backgrounds and much more. In NYX NoCode the styles are included inside the HTML file,
                but they could be moved to a css file. NYX NoCode uses Python for jobs like plotting graphs and it can use Flask to set up web pages if specified.
                We don't know what all NYX NoCode will do just yet.
            </p>

            <p><strong>What natural languages do NYX NoCode and Justin Bearcat speak?</strong></p>
            <p>
                Quite a few! In addition to English, we have tried Spanish, French, Italian, Hindi, and Russian. There are many more, just ask the AI to do it.
            </p>

            <p><strong>Can I use my own files?</strong></p>
            <p>
                Yes. Just drag and drop them into the Gallery's File Upload box, or insert any web link as part of your prompt to link to that page.
            </p>

            <p><strong>Is there a best way to talk to an LLM?</strong></p>
            <p>
                Yes. Be respectful to the AI and talk with it in the same way that you would like to be spoken to. While this is not a real person,
                it has been trained on the knowledge of real people. If you are nice and polite when you interact with NYX NoCode and Justin Bearcat,
                you will get the best results (please and thank you go a long way).
            </p>

            <p><strong>Can I make interactive and animated content, like a simulation?</strong></p>
            <p>
                Yes. Simply use words like those in your description. Instead of asking for a gradient background, ask for an animated gradient background.
                Instead of just asking for an animated bouncing ball, ask for an interactive animated bouncing ball where you click to start and click again to stop.
                The possibilities are just about endless. You can even invent new ways for content to appear, like a slide show where the images spin and flash.
            </p>

            <p><strong>Can I make complex pages and applications?</strong></p>
            <p>
                Yes. The best way to start is a little bit a time. Remember, you can make less complex items and then link them using NYX NoCode
                (just supply the links to the pages you make in a new prompt).
            </p>

            <p><strong>Is this private?</strong></p>
            <p>
                Yes. We do not collect any personal information at all on student hackathon participants. The AI chat conversations are done anonymously
                and are not saved in the system. Microsoft can see the system and will censor inappropriate requests.
            </p>

            <p><strong>Why is this called a hackathon?</strong></p>
            <p>
                While 'hacker' is often used as a negative term used to refer to a bad actor that breaks into systems, in this case it is used in its original form,
                which means someone who is testing the boundaries of a system to make new discoveries.
            </p>

            <p><strong>NYX Support:</strong></p>
            <p>
                Reach out to your teacher or ask Justin Bearcat for help if you get stuck!
            </p>

            <h2>Important Tips</h2>
            <p><strong>Remember to Save!</strong></p>
            <ul>
                <li>Always click "Save Prompt" before you hit Send if you want to keep your prompt</li>
                <li>Doodles, images and web pages are automatically saved to the Gallery</li>
            </ul>

            <p><strong>Don't Be Afraid to Experiment!</strong></p>
            <p>
                There's no wrong way to use NYX NoCode. Try different things, see what works, and have fun! If something doesn't turn out
                the way you expected, just try again with a different prompt or approach.
            </p>

            <p><strong>Working in Teams:</strong></p>
            <p>
                Consider assigning roles in your team, even if you are wearing more than one hat:
            </p>
            <ul>
                <li>Project Manager - Keeps everyone organized</li>
                <li>Designer - Focuses on how things look</li>
                <li>Content Creator - Writes prompts and creates doodles</li>
                <li>QA Tester - Makes sure everything works well</li>
            </ul>

            <h2>More Inspiration</h2>
            <p>Check out these galleries for even more ideas:</p>
            <ul>
                <li><a href={"https://nyx.baby/NYX-Catalog"} rel="noreferrer" target={"_blank"}>NYX Catalog</a> – Browse through last year's student projects and creations</li>
                <li><a href={"https://nyx.baby/demos"} rel="noreferrer" target={"_blank"}>Interactive Examples Gallery</a> – Explore new interactive examples and demos</li>
            </ul>

            <h2>Examples</h2>
            <p>Check out these cool examples to see what's possible with NYX NoCode!</p>
            <ul>
                <li><a href={"https://drive.nyx.baby/nyxnocode/Team001@mena.nyxnocode.com/Prompt_add_below_my_first_prompt_To_get_the_other_features_I_kept_clicking_th-20241105-061759.html"} rel="noreferrer" target={"_blank"}>Bouncing, Moving 3D Spheres</a> – This page includes a video demo (no sound), a link to the demo, a link to the final text prompt, and a link to the original text prompt and original demo.</li>
                <li><a href={"https://drive.nyx.baby/nyxnocode/kilroy@uark.edu/add_some_3d_cubes_that_are_spinningDOCTYPE_html_html_langen_head_meta-20251102-071737.html"} rel="noreferrer" target={"_blank"}>Spinning 3D Dice-like Cubes</a> – Prompt: "Create an effect with css that I have probably never seen before." The starry background and the mouse cursor that leaves trails came back. Then the html was copied back into the prompt, with another prompt saying to add the cubes, then another time to make them dice-like.</li>
                <li><a href={"https://drive.nyx.baby/nyxnocode/kilroy@uark.edu/Create_a_webpage_featuring_a_3D_spinning_cube_with_a_different_video_embedded_o-20251025-101856.html"} rel="noreferrer" target={"_blank"}>3D Spinning Cube with Videos</a> – Prompt: "Create a webpage featuring a 3D spinning cube with a different video embedded on each side." I had previously generated several videos using the Video Generator on the Prompt Pad, then selected them from the Gallery, and then went to Prompt Pad and entered this prompt. (Only 4 videos were selected so it used one twice.)</li>
                <li><a href={"https://drive.nyx.baby/nyxnocode/kilroy@uark.edu/here_is_the_missing_image_when_you_click_Meet_Major_Geo_this_should_come_up_ht-20251026-070545.html"} rel="noreferrer" target={"_blank"}>Meet Major Geo</a> – His background is a looping video made with the Video Generator. It plays forward, then backwards to make it look smooth in a continuous loop. When you click "Meet Major Geo" you get an example of an image that comes up in a shadowbox. You can close the shadowbox by clicking the X in the upper right hand corner.</li>
                <li><a href={"https://drive.nyx.baby/nyxnocode/Team001@mena.nyxnocode.com/animated_background_with_bright_colors_that_says_welcome_to_the_party-20241104-175015.html"} rel="noreferrer" target={"_blank"}>Welcome to the Party - backlighted animated image</a> – Prompt: "Animated background colors with a sign that says Welcome to the Party!"</li>
                <li><a href={"https://nyxblob.blob.core.windows.net/nyxnocode/kilroy@uark.edu/interactive_simulation_of_a_rain_storm-20241103-015816.html"} rel="noreferrer" target={"_blank"}>Rain Simulator</a> – Prompt: Interactive simulation of a rainstorm</li>
                <li><a href={"https://drive.nyx.baby/nyxnocode/Team001@mena.nyxnocode.com/interactive_3d_globe_that_I_can_spin_please-20241105-221515.html"} rel="noreferrer" target={"_blank"}>Earth-style animation</a> – Prompt: Interactive 3d globe that I can spin.</li>
                <li><a href={"https://drive.nyx.baby/nyxnocode/Team001@mena.nyxnocode.com/3d_rendered_ball_that_bounces_around_the_screen_with_a_light_that_shines_on_it_-20241105-213416.html"} rel="noreferrer" target={"_blank"}>Spotlight</a> – Prompt: 3d Rendered Ball that Bounces Around the Screen.</li>
                <li><a href={"https://drive.nyx.baby/nyxnocode/Team001@mena.nyxnocode.com/animated_moving_search_lights_across_a_mountain_town_with_a_mountain_bike_in_the-20241105-213617.html"} rel="noreferrer" target={"_blank"}>Spotlights over the Trails of Mena</a> – Prompt: Animated seach lights moving across a mountain town with a mountain bike in the foreground.</li>
                <li><a href={"https://nyxblob.blob.core.windows.net/nyxnocode/kilroy@uark.edu/make_a_flask_page_showcasing_my_dog_sitting_service_for_mountain_bikers-20241103-035544.html"} rel="noreferrer" target={"_blank"}>Dog Sitting Service</a> – Prompt: Make a flask page showcasing my dogsitting service for mountain bikers.</li>
                <li><a href={"https://nyxblob.blob.core.windows.net/nyxnocode/kilroy@uark.edu/httpsnyxblobblobcorewindowsnetnyxnocodekilroyuarkedu1730630044995do-20241103-043507.html"} rel="noreferrer" target={"_blank"}>Colorful string of lights</a> – This colorful string of lights was created with the Doodle Pad, which generated a prompt and the background image.</li>
                <li><a href={"https://nyxblob.blob.core.windows.net/nyxnocode/kilroy@uark.edu/the_best_new_thing_youve_seen_all_day-20241103-084451.html"} rel="noreferrer" target={"_blank"}>Our Hackathon Students are Inspirational</a> – Prompt: The best new thing you've seen all day</li>
                <li><a href={"https://drive.nyx.baby/nyxnocode/Team001@mena.nyxnocode.com/Mena_Mountain_Bike_Sticker_Store_Please_fill_in_details_for_the_store_simulation-20241105-070144.html"} rel="noreferrer" target={"_blank"}>Mena Mountain Bike Sticker Store</a> – Each sticker was made with a separate prompt. It took a few tries to for Mena to be spelled correctly. Prompt: Build a Mena Sticker Store simulation. Plus I had selected each pre-built sticker from the gallery, and clicked the icons so the urls go into the prompt.</li>
                <li><a href={"https://nyxblob.blob.core.windows.net/nyxnocode/kilroy%40uark.edu%2Fa_form_to_help_me_plan_my_trip_to_Mena_Arkansas_that_lets_me_select_places_to_v-20241031-033509.html"} rel="noreferrer" target={"_blank"}>Mena Trip Planner</a> – Prompt: a form to help me plan a trip to Mena, Arkansas that lets me select places to visit, add the number of days and my name, and print the list.</li>
                <li><a href={"https://drive.nyx.baby/nyxnocode/Team001@mena.nyxnocode.com/httpsdrivenyxbabynyxnocodeTeam001menanyxnocodecomdoodles173082003664-20241105-092305.html"} rel="noreferrer" target={"_blank"}>Image Recognition of a Hamster Drawing</a> – This shows the trail that is automatically created as a sketch is done on the Doodle Pad. The caption is what image recognition AI guesses the picture is each step of the way. These are really easy to make, and what happens when you click Generate after you draw something and don't clear the prompt box.</li>
                <li><a href={"https://drive.nyx.baby/nyxnocode/Team001@mena.nyxnocode.com/Create_an_interactive_app_with_a_clickable_spinning_wheel_that_blends_colors_as_-20241113-170734.html"} rel="noreferrer" target={"_blank"}>Interactive Spinning Color Wheel</a> – Prompt: Create an interactive app with a clickable spinning wheel that blends colors as it spins. The wheel should have multiple segments, each with a different color. When clicked, the wheel spins smoothly and creates a whirl of blended colors that's visually appealing. Each time I click it, make it go faster and faster. When I click the Reverse Button, reverse it. When I click stop, stop it.</li>
                <li><a href={"https://drive.nyx.baby/nyxnocode/Team001@mena.nyxnocode.com/flash_these_fast_like_an_animation_httpsdrivenyxbabynyxnocodeTeam00140m-20241116-205651.html"} rel="noreferrer" target={"_blank"}>Animate Your Doodles</a> – Prompt: (after selecting the doodles from the Gallery and then clicking them in the Prompt Box) Flash these fast, like an animation.</li>
                <li><a href={"https://drive.nyx.baby/nyxnocode/Team001@mena.nyxnocode.com/what_if_outer_space_aliens_rode_bikes_in_Mena-20241116-045005.html"} rel="noreferrer" target={"_blank"}>What If?</a> – Prompt: What If Outer Space Aliens Rode Bikes in Mena? (Here I made up a tall tale asking a silly question.)</li>
                <li><a href={"https://drive.nyx.baby/nyxnocode/Team001@mena.nyxnocode.com/Trails_at_Mena_water_bottle-20241116-052419.html"} rel="noreferrer" target={"_blank"}>Trails at Mena Water Bottle</a> – Prompt: Trails at Mena Water Bottle. Here I made up a pretend product that might be sold in the region.</li>
                <li><a href={"https://drive.nyx.baby/nyxnocode/Team001@mena.nyxnocode.com/add_this_mp3_as_the_music_xo_httpsdrivenyxbabynyxnocodeTeam00140menanyx-20241118-090157.html"} rel="noreferrer" target={"_blank"}>Happy Holidays Slide Show with Music</a> – This took me a few rounds. First I made the images. Then I selected the images from the Gallery. Then I went into the Prompt Lab and selected all the images. I asked NYX Nocode to make a slide show with spinning images, and a music player that you can start and stop. The music is downloaded from the Library of Congress' website and uploaded to NYX NoCode.</li>
                <li><a href={"https://drive.nyx.baby/nyxnocode/Team001@mena.nyxnocode.com/Red_Bearcat_Beanie_Bag_Character_dressed_as_an_Astronaut-20241126-040816.html"} rel="noreferrer" target={"_blank"}>Make Your Own Characters</a> – Prompt: Red Bearcat Beanie Bag Character Dressed as an Astronaut</li>
            </ul>

            <h2>Additional Video Lessons</h2>
            <p>Watch these video lessons to learn more about using NYX NoCode:</p>

            <div style={{textAlign: 'center', margin: '20px 0'}}>
                <iframe
                    width="560"
                    height="315"
                    src="https://www.youtube-nocookie.com/embed/7LFYhEEVbGE?list=PLjQ8Tc7k38HmOysh4ZUa8DHAM53q-S4gt&enablejsapi=1&rel=0"
                    title="NYX NoCode Video Lessons"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                    style={{maxWidth: '100%', borderRadius: '8px'}}
                ></iframe>
            </div>

            <h2>Final Thoughts</h2>
            <p>
                Remember: You're not just building projects—you're learning valuable skills in prompt engineering, design thinking,
                and creative problem-solving. These skills will help you in the real world!
            </p>
            <p>
                Be creative, and don't be afraid to push the boundaries of what NYX NoCode can do.
                Make sure you tell the teacher what you think about NYX NoCode, because that can help make the platform better for everyone.
            </p>

            <p style={{marginTop: '40px', fontStyle: 'italic', textAlign: 'center'}}>
                Most of all, have fun expressing and sharing your ideas!
            </p>
        </div>
    );
};

export default NyxNoCodeStudentGuide;
